import React, { useState, useEffect } from 'react';
import AdPlaceholder from './AdPlaceholder';
import './Calculator.css';

const gradePointsMap = {
  S: 10,
  A: 9,
  B: 8,
  C: 7,
  D: 6,
  E: 5,
  F: 0,
  N: 0
};

const gradeColors = {
  S: '#10b981',
  A: '#3b82f6',
  B: '#8b5cf6',
  C: '#f59e0b',
  D: '#f97316',
  E: '#ef4444',
  F: '#991b1b',
  N: '#6b7280'
};

const Calculator = () => {
  const [calculatorMode, setCalculatorMode] = useState('cgpa'); // cgpa, detailed, target
  
  // Separate state for Quick CGPA mode
  const [quickSemesters, setQuickSemesters] = useState([
    {
      id: 1,
      name: 'Semester 1',
      gpa: '',
      credits: ''
    }
  ]);
  
  // Separate state for Detailed Tracker mode
  const [semesters, setSemesters] = useState([
    {
      id: 1,
      name: 'Semester 1',
      courses: [{ id: 1, name: '', credits: '', grade: 'S' }]
    }
  ]);
  
  const [result, setResult] = useState(null);
  const [existingCgpa, setExistingCgpa] = useState('');
  const [existingCredits, setExistingCredits] = useState('');
  const [targetCgpa, setTargetCgpa] = useState('');
  const [remainingCredits, setRemainingCredits] = useState('');
  const [targetResult, setTargetResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    quick: {},
    detailed: {},
    target: {}
  });

  // Load saved data on component mount
  useEffect(() => {
    loadSavedData();
  }, []);

  // Auto-calculate when Quick CGPA data changes
  useEffect(() => {
    if (calculatorMode === 'cgpa') {
      calculateDetailedCGPA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSemesters, calculatorMode]);

  // Auto-calculate when Detailed Tracker data changes
  useEffect(() => {
    if (calculatorMode === 'detailed') {
      calculateDetailedCGPA();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semesters, calculatorMode]);

  // Auto-save whenever semesters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveData();
    }, 1000); // Debounce: save 1 second after last change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semesters, quickSemesters]);

  const loadSavedData = async () => {
    try {
      // Load from localStorage
      const saved = localStorage.getItem('vitCgpaData');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.semesters) setSemesters(data.semesters);
        if (data.quickSemesters) setQuickSemesters(data.quickSemesters);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  const saveData = async () => {
    try {
      // Save to localStorage
      localStorage.setItem('vitCgpaData', JSON.stringify({ semesters, quickSemesters }));
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  // Add Semester
  const addSemester = () => {
    const newSemester = {
      id: Date.now(),
      name: `Semester ${semesters.length + 1}`,
      courses: [{ id: Date.now(), name: '', credits: '', grade: 'S' }]
    };
    setSemesters([...semesters, newSemester]);
  };

  // Quick CGPA: Add Semester
  const addQuickSemester = () => {
    const newSemester = {
      id: Date.now(),
      name: `Semester ${quickSemesters.length + 1}`,
      gpa: '',
      credits: ''
    };
    setQuickSemesters([...quickSemesters, newSemester]);
  };

  // Quick CGPA: Remove Semester
  const removeQuickSemester = (semId) => {
    if (quickSemesters.length > 1) {
      setQuickSemesters(quickSemesters.filter((sem) => sem.id !== semId));
      setValidationErrors((prev) => {
        const nextQuick = { ...prev.quick };
        delete nextQuick[semId];
        return { ...prev, quick: nextQuick };
      });
    }
  };

  // Quick CGPA: Update Semester
  const updateQuickSemester = (semId, field, value) => {
    setQuickSemesters(
      quickSemesters.map((sem) => (sem.id === semId ? { ...sem, [field]: value } : sem))
    );
    
    // Clear validation error for this field
    setValidationErrors((prev) => {
      const updated = { ...prev };
      if (updated.quick[semId]?.[field]) {
        const semErrors = { ...updated.quick[semId] };
        delete semErrors[field];
        if (Object.keys(semErrors).length === 0) {
          const nextQuick = { ...updated.quick };
          delete nextQuick[semId];
          updated.quick = nextQuick;
        } else {
          updated.quick = { ...updated.quick, [semId]: semErrors };
        }
      }
      return updated;
    });
  };

  // Remove Semester
  const removeSemester = (semId) => {
    if (semesters.length > 1) {
      setSemesters(semesters.filter((sem) => sem.id !== semId));
      setResult(null);
      setValidationErrors((prev) => {
        const nextQuick = { ...prev.quick };
        delete nextQuick[semId];
        const nextDetailed = { ...prev.detailed };
        delete nextDetailed[semId];
        return { ...prev, quick: nextQuick, detailed: nextDetailed };
      });
    }
  };

  // Update Semester Name
  const updateSemesterName = (semId, name) => {
    setSemesters(
      semesters.map((sem) => (sem.id === semId ? { ...sem, name } : sem))
    );
  };

  // Add Course to Semester
  const addCourse = (semId) => {
    setSemesters(
    semesters.map((sem) =>
        sem.id === semId
          ? {
              ...sem,
              courses: [
                ...sem.courses,
                { id: Date.now(), name: '', credits: '', grade: 'S' }
              ]
            }
          : sem
      )
    );
  };

  // Remove Course
  const removeCourse = (semId, courseId) => {
    setSemesters(
      semesters.map((sem) =>
        sem.id === semId
          ? {
              ...sem,
              courses: sem.courses.filter((course) => course.id !== courseId)
            }
          : sem
      )
    );
    setValidationErrors((prev) => {
      const nextDetailed = { ...prev.detailed };
      if (nextDetailed[semId]) {
        const courseErrors = { ...nextDetailed[semId] };
        delete courseErrors[courseId];
        if (Object.keys(courseErrors).length === 0) {
          delete nextDetailed[semId];
        } else {
          nextDetailed[semId] = courseErrors;
        }
      }
      return { ...prev, detailed: nextDetailed };
    });
  };

  // Update Course
  const updateCourse = (semId, courseId, field, value) => {
    setSemesters(
      semesters.map((sem) =>
        sem.id === semId
          ? {
              ...sem,
              courses: sem.courses.map((course) =>
                course.id === courseId ? { ...course, [field]: value } : course
              )
            }
          : sem
      )
    );
    setResult(null);

    setValidationErrors((prev) => {
      const updated = { ...prev };

      if (updated.quick[semId]) {
        const semErrors = { ...updated.quick[semId] };
        const quickField = field === 'name' ? 'gpa' : field;
        if (semErrors[quickField]) {
          delete semErrors[quickField];
          if (Object.keys(semErrors).length === 0) {
            const nextQuick = { ...updated.quick };
            delete nextQuick[semId];
            updated.quick = nextQuick;
          } else {
            updated.quick = { ...updated.quick, [semId]: semErrors };
          }
        }
      }

      if (updated.detailed[semId]?.[courseId]?.[field]) {
        const semErrors = { ...updated.detailed[semId] };
        const courseErrors = { ...semErrors[courseId] };
        delete courseErrors[field];
        if (Object.keys(courseErrors).length === 0) {
          delete semErrors[courseId];
        } else {
          semErrors[courseId] = courseErrors;
        }
        if (Object.keys(semErrors).length === 0) {
          const nextDetailed = { ...updated.detailed };
          delete nextDetailed[semId];
          updated.detailed = nextDetailed;
        } else {
          updated.detailed = { ...updated.detailed, [semId]: semErrors };
        }
      }

      return updated;
    });
  };

  // Calculate Detailed CGPA
  const calculateDetailedCGPA = () => {
    // For Quick CGPA mode - use GPA directly
    if (calculatorMode === 'cgpa') {
      let totalWeightedGpa = 0;
      let totalCredits = 0;
      let isValid = true;
      const quickErrors = {};

      quickSemesters.forEach((sem) => {
        const semGpa = parseFloat(sem.gpa ?? '');
        const semCredits = parseFloat(sem.credits ?? '');
        const semError = {};

        if (isNaN(semGpa) || semGpa < 0 || semGpa > 10) {
          semError.gpa = 'Enter GPA between 0 and 10.';
          isValid = false;
        }

        if (isNaN(semCredits) || semCredits <= 0) {
          semError.credits = 'Enter credits greater than 0.';
          isValid = false;
        }

        if (Object.keys(semError).length > 0) {
          quickErrors[sem.id] = semError;
        } else {
          totalWeightedGpa += semGpa * semCredits;
          totalCredits += semCredits;
        }
      });

      if (!isValid || totalCredits === 0) {
        setValidationErrors((prev) => ({
          ...prev,
          quick: quickErrors
        }));
        setResult(null);
        return;
      }

      setValidationErrors((prev) => ({
        ...prev,
        quick: {}
      }));

      const cgpa = totalWeightedGpa / totalCredits;
      const percentage = cgpa * 10;

      setResult({
        cgpa: cgpa.toFixed(2),
        percentage: percentage.toFixed(2),
        totalCredits,
        semesterResults: quickSemesters.map((sem) => ({
          name: sem.name,
          gpa: parseFloat(sem.gpa || 0).toFixed(2),
          credits: parseFloat(sem.credits || 0)
        })),
        overallGradeDistribution: {}
      });
      return;
    }

    // For Detailed mode - calculate from grades
    let totalGradePoints = 0;
    let totalCredits = 0;
    const semesterResults = [];
    let isValid = true;
    const detailedErrors = {};

    semesters.forEach((sem) => {
      let semGradePoints = 0;
      let semCredits = 0;
      const gradeDistribution = {};

      sem.courses.forEach((course) => {
        const credits = parseFloat(course.credits);
        const gradePoint = gradePointsMap[course.grade];

        // Course name is optional, only check credits
        if (isNaN(credits) || credits <= 0) {
          isValid = false;
          detailedErrors[sem.id] = detailedErrors[sem.id] || {};
          detailedErrors[sem.id][course.id] = {
            ...(detailedErrors[sem.id][course.id] || {}),
            credits: 'Enter credits greater than 0.'
          };
          return;
        }

        semGradePoints += gradePoint * credits;
        semCredits += credits;

        // Grade distribution
        gradeDistribution[course.grade] =
          (gradeDistribution[course.grade] || 0) + 1;
      });

      const semGpa = semCredits > 0 ? semGradePoints / semCredits : 0;

      semesterResults.push({
        name: sem.name,
        gpa: semGpa.toFixed(2),
        credits: semCredits,
        gradeDistribution
      });

      totalGradePoints += semGradePoints;
      totalCredits += semCredits;
    });

    if (!isValid || totalCredits === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        detailed: detailedErrors
      }));
      setResult(null);
      return;
    }

    setValidationErrors((prev) => ({
      ...prev,
      detailed: {}
    }));

    const cgpa = totalGradePoints / totalCredits;
    const percentage = cgpa * 10;

    // Overall grade distribution
    const overallGradeDistribution = {};
    semesters.forEach((sem) => {
      sem.courses.forEach((course) => {
        overallGradeDistribution[course.grade] =
          (overallGradeDistribution[course.grade] || 0) + 1;
      });
    });

    setResult({
      cgpa: cgpa.toFixed(2),
      percentage: percentage.toFixed(2),
      totalCredits,
      semesterResults,
      overallGradeDistribution
    });

    saveData();
  };

  // Calculate Target Grade Needed
  const calculateTargetGrade = () => {
    const current = parseFloat(existingCgpa);
    const currentCreds = parseFloat(existingCredits);
    const target = parseFloat(targetCgpa);
    const remaining = parseFloat(remainingCredits);

    const targetErrors = {};

    if (isNaN(current) || current < 0 || current > 10) {
      targetErrors.currentCgpa = 'Enter current CGPA between 0 and 10.';
    }

    if (isNaN(currentCreds) || currentCreds < 0) {
      targetErrors.existingCredits = 'Enter completed credits (0 or more).';
    }

    if (isNaN(target) || target < 0 || target > 10) {
      targetErrors.targetCgpa = 'Enter target CGPA between 0 and 10.';
    } else if (!isNaN(current) && target < current) {
      targetErrors.targetCgpa = 'Target CGPA must be at least your current CGPA.';
    }

    if (isNaN(remaining) || remaining <= 0) {
      targetErrors.remainingCredits = 'Enter remaining credits greater than 0.';
    }

    if (Object.keys(targetErrors).length > 0) {
      setValidationErrors((prev) => ({
        ...prev,
        target: targetErrors
      }));
      setTargetResult(null);
      return;
    }

    setValidationErrors((prev) => ({
      ...prev,
      target: {}
    }));

    const currentTotalGradePoints = current * currentCreds;
    const totalFutureCredits = currentCreds + remaining;
    const requiredTotalGradePoints = target * totalFutureCredits;
    const requiredGradePoints = requiredTotalGradePoints - currentTotalGradePoints;
    const requiredGPA = requiredGradePoints / remaining;

    let achievable = requiredGPA <= 10;
    let requiredGrade = 'S';

    if (requiredGPA >= 9.5) requiredGrade = 'S';
    else if (requiredGPA >= 8.5) requiredGrade = 'A';
    else if (requiredGPA >= 7.5) requiredGrade = 'B';
    else if (requiredGPA >= 6.5) requiredGrade = 'C';
    else if (requiredGPA >= 5.5) requiredGrade = 'D';
    else if (requiredGPA >= 4.5) requiredGrade = 'E';
    else requiredGrade = 'F+';

    setTargetResult({
      requiredGPA: requiredGPA.toFixed(2),
      requiredGrade,
      achievable,
      message: achievable
        ? `You need an average GPA of ${requiredGPA.toFixed(2)} (approximately ${requiredGrade} grade) in the remaining ${remaining} credits to achieve your target CGPA of ${target}.`
        : `Achieving a CGPA of ${target} is not possible even with perfect scores (10 GPA) in all remaining courses. Maximum achievable CGPA: ${((currentTotalGradePoints + 10 * remaining) / totalFutureCredits).toFixed(2)}`
    });
  };

  // Clear All Data
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      setSemesters([
        {
          id: 1,
          name: 'Semester 1',
          courses: [{ id: 1, name: '', credits: '', grade: 'S' }]
        }
      ]);
      setQuickSemesters([
        {
          id: 1,
          name: 'Semester 1',
          gpa: '',
          credits: ''
        }
      ]);
      setResult(null);
      localStorage.removeItem('vitCgpaData');
      setValidationErrors({ quick: {}, detailed: {}, target: {} });
    }
  };

  return (
    <div className="calculator-page">
      <AdPlaceholder position="top" />

      <div className="calculator-container-new">
        <div className="calculator-header-new">
          <div className="header-content">
            <h1>🎓 VIT CGPA Calculator</h1>
            <p className="subtitle">Advanced GPA & CGPA Calculator with Analytics</p>
          </div>

          {/* Mode Selector */}
          <div className="mode-selector">
            <button
              className={`mode-btn ${calculatorMode === 'cgpa' ? 'active' : ''}`}
              onClick={() => setCalculatorMode('cgpa')}
            >
              📊 Quick CGPA
            </button>
            <button
              className={`mode-btn ${calculatorMode === 'detailed' ? 'active' : ''}`}
              onClick={() => setCalculatorMode('detailed')}
            >
              📚 Detailed Tracker
            </button>
            <button
              className={`mode-btn ${calculatorMode === 'target' ? 'active' : ''}`}
              onClick={() => setCalculatorMode('target')}
            >
              🎯 Target Calculator
            </button>
          </div>
        </div>

        {/* Quick CGPA Mode */}
        {calculatorMode === 'cgpa' && (
          <div className="mode-content">
            <div className="mode-description">
              <h2>Quick CGPA Calculator</h2>
              <p>Enter your semester-wise GPA and credits to calculate overall CGPA</p>
            </div>

            <div className="semesters-grid">
              {quickSemesters.map((semester, index) => (
                <div key={semester.id} className="semester-card-simple">
                  <div className="semester-card-header">
                    <h3>{semester.name}</h3>
                    {quickSemesters.length > 1 && (
                      <button
                        className="remove-btn"
                        onClick={() => removeQuickSemester(semester.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="semester-inputs-simple">
                    {(() => {
                      const quickErrors = validationErrors.quick[semester.id] || {};
                      return (
                        <>
                    <div className="input-group">
                      <label>Semester GPA</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.01"
                        placeholder="e.g., 8.50"
                        value={semester.gpa || ''}
                        onChange={(e) =>
                          updateQuickSemester(semester.id, 'gpa', e.target.value)
                        }
                      />
                      {quickErrors.gpa && (
                        <span className="helper-text error">{quickErrors.gpa}</span>
                      )}
                    </div>
                    <div className="input-group">
                      <label>Total Credits</label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        placeholder="e.g., 20"
                        value={semester.credits || ''}
                        onChange={(e) =>
                          updateQuickSemester(semester.id, 'credits', e.target.value)
                        }
                      />
                      {quickErrors.credits && (
                        <span className="helper-text error">{quickErrors.credits}</span>
                      )}
                    </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>

            <div className="actions-row">
              <button className="secondary-btn" onClick={addQuickSemester}>
                + Add Semester
              </button>
            </div>
          </div>
        )}

        {/* Detailed Tracker Mode */}
        {calculatorMode === 'detailed' && (
          <div className="mode-content">
            <div className="mode-description">
              <h2>Detailed Course Tracker</h2>
              <p>Track all your courses with individual grades and credits</p>
            </div>

            {semesters.map((semester) => (
              <div key={semester.id} className="semester-section">
                <div className="semester-title-row">
                  <input
                    type="text"
                    className="semester-name-input"
                    value={semester.name}
                    onChange={(e) => updateSemesterName(semester.id, e.target.value)}
                  />
                  {semesters.length > 1 && (
                    <button
                      className="remove-semester-btn"
                      onClick={() => removeSemester(semester.id)}
                      title="Remove Semester"
                    >
                      × Remove Semester
                    </button>
                  )}
                </div>

                <div className="courses-table">
                  <div className="table-header">
                    <div>#</div>
                    <div>Course Name (Optional)</div>
                    <div>Credits</div>
                    <div>Grade</div>
                    <div></div>
                  </div>

                  {semester.courses.map((course, idx) => (
                    <div key={course.id} className="course-row-detailed">
                      <div className="course-number">{idx + 1}</div>
                      <input
                        type="text"
                        placeholder="e.g., Data Structures (Optional)"
                        value={course.name}
                        onChange={(e) =>
                          updateCourse(semester.id, course.id, 'name', e.target.value)
                        }
                      />
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.5"
                        placeholder="Credits"
                        value={course.credits}
                        onChange={(e) =>
                          updateCourse(semester.id, course.id, 'credits', e.target.value)
                        }
                      />
                      <select
                        value={course.grade}
                        onChange={(e) =>
                          updateCourse(semester.id, course.id, 'grade', e.target.value)
                        }
                        style={{
                          backgroundColor: gradeColors[course.grade] + '20',
                          borderColor: gradeColors[course.grade]
                        }}
                      >
                        {Object.keys(gradePointsMap).map((grade) => (
                          <option key={grade} value={grade}>
                            {grade} ({gradePointsMap[grade]})
                          </option>
                        ))}
                      </select>
                      {semester.courses.length > 1 && (
                        <button
                          className="remove-course-icon-btn"
                          onClick={() => removeCourse(semester.id, course.id)}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  className="add-course-btn"
                  onClick={() => addCourse(semester.id)}
                >
                  + Add Course
                </button>
              </div>
            ))}

            <div className="actions-row">
              <button className="secondary-btn" onClick={addSemester}>
                + Add Semester
              </button>
              <button className="danger-btn" onClick={clearAllData}>
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Target Grade Calculator */}
        {calculatorMode === 'target' && (
          <div className="mode-content">
            <div className="mode-description">
              <h2>🎯 Target CGPA Calculator</h2>
              <p>Calculate what grades you need to achieve your target CGPA</p>
            </div>

            <div className="target-grid">
              <div className="target-input-card">
                <h3>Current Status</h3>
                <div className="input-group">
                  <label>Current CGPA</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.01"
                    placeholder="e.g., 7.50"
                    value={existingCgpa}
                    onChange={(e) => setExistingCgpa(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label>Completed Credits</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g., 120"
                    value={existingCredits}
                    onChange={(e) => setExistingCredits(e.target.value)}
                  />
                </div>
              </div>

              <div className="target-input-card">
                <h3>Your Goal</h3>
                <div className="input-group">
                  <label>Target CGPA</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.01"
                    placeholder="e.g., 8.00"
                    value={targetCgpa}
                    onChange={(e) => setTargetCgpa(e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label>Remaining Credits</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g., 40"
                    value={remainingCredits}
                    onChange={(e) => setRemainingCredits(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button className="primary-btn full-width" onClick={calculateTargetGrade}>
              Calculate Required Grade
            </button>

            {targetResult && (
              <div className={`target-result ${targetResult.achievable ? 'achievable' : 'not-achievable'}`}>
                <div className="target-result-header">
                  <span className="result-icon">
                    {targetResult.achievable ? '✅' : '⚠️'}
                  </span>
                  <h3>{targetResult.achievable ? 'Goal Achievable!' : 'Goal Not Achievable'}</h3>
                </div>
                <div className="target-result-body">
                  <div className="required-grade-display">
                    <span className="label">Required Average GPA:</span>
                    <span className="value" style={{
                      color: targetResult.achievable ? '#10b981' : '#ef4444'
                    }}>
                      {targetResult.requiredGPA}
                    </span>
                    <span className="grade-badge" style={{
                      backgroundColor: gradeColors[targetResult.requiredGrade.charAt(0)] || '#6b7280'
                    }}>
                      {targetResult.requiredGrade}
                    </span>
                  </div>
                  <p className="target-message">{targetResult.message}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        {result && (calculatorMode === 'detailed' || calculatorMode === 'cgpa') && (
          <div className="results-minimal">
            <div className="results-cards">
              <div className="result-card">
                <div className="result-label">CGPA</div>
                <div className="result-value">{result.cgpa}</div>
              </div>
              <div className="result-card">
                <div className="result-label">Total Credits</div>
                <div className="result-value">{result.totalCredits}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <AdPlaceholder position="bottom" />
    </div>
  );
};

export default Calculator;
